import dayjs from 'dayjs';
import {
  getMonthlyPostingFinalizedByDisplayName,
  getMonthlyPostingProductName,
} from './monthlyFinalizedPostingHelpers';
import { firstProvided, hasProvidedValue, normalizeCreditCardValues } from '../../../../utils/valueSelection';

/**
 * @typedef {Object} CreatedByUser
 * @property {number} id
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string|null} username
 */

/**
 * @typedef {Object} WeeklyPosting
 * @property {string} id
 * @property {string} postingId
 * @property {string} x3ProductName
 * @property {number} cash
 * @property {number} credit
 * @property {number} refund
 * @property {number} total
 * @property {string} startDate
 * @property {string} endDate
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {CreatedByUser} createdByUser
 * @property {{ id: number; name: string }[]} [sublocations]
 */

/**
 * @typedef {Object} MonthlyPosting
 * @property {string} id
 * @property {string} postingId
 * @property {string} x3ProductName
 * @property {string} startDate
 * @property {string} endDate
 * @property {string} [creditCardCurrent]
 * @property {string} [creditCardPosted]
 * @property {string} [ccCurrent]
 * @property {string} [ccPosted]
 * @property {string} [creditVariance]
 * @property {string} [cashCurrent]
 * @property {string} [cashPosted]
 * @property {string} [cashVariance]
 * @property {string} [totalCurrent]
 * @property {string} [totalVariance]
 * @property {string} createdAt
 * @property {CreatedByUser} createdByUser
 * @property {WeeklyPosting[]} [weeklyPostings]
 */

const DATE_DISPLAY = 'MM/DD/YYYY';
const TABLE_DATE_DISPLAY = 'MM/DD';
const DATE_TIME_DISPLAY = 'MM/DD/YYYY hh:mm A';

export const formatPostingDate = (value) => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format(DATE_DISPLAY) : '—';
};

export const formatDateRange = (startDate, endDate) => {
  const start = startDate ? dayjs(startDate) : null;
  const end = endDate ? dayjs(endDate) : null;
  if (!start?.isValid() && !end?.isValid()) return '—';
  const startStr = start?.isValid() ? start.format(TABLE_DATE_DISPLAY) : '—';
  const endStr = end?.isValid() ? end.format(TABLE_DATE_DISPLAY) : '—';
  return `${startStr} – ${endStr}`;
};

const toNumber = (v) => {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toOptionalNumber = (v) => (hasProvidedValue(v) ? toNumber(v) : null);

const dedupeSublocations = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const id = item?.id;
    if (id == null || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const getAvailableSublocations = (posting) => {
  if (Array.isArray(posting?.availableSublocations) && posting.availableSublocations.length > 0) {
    return dedupeSublocations(posting.availableSublocations);
  }

  const weeklyPostings = Array.isArray(posting?.weeklyPostings) ? posting.weeklyPostings : [];

  return dedupeSublocations(
    weeklyPostings.flatMap((weeklyPosting) =>
      Array.isArray(weeklyPosting?.sublocations) ? weeklyPosting.sublocations : [],
    ),
  );
};

const buildSublocationState = (selectedItems, availableItems) => {
  const selected = dedupeSublocations(selectedItems);
  const available = dedupeSublocations(availableItems);
  const selectedIds = new Set(selected.map((item) => item.id));
  const unselected = available.filter((item) => !selectedIds.has(item.id));
  const total = available.length;

  return {
    isIncluded: total > 0 ? `${selected.length}/${total}` : '—',
    sublocations: {
      selected,
      unselected,
      total,
    },
  };
};

export const buildTableRowsFromPosting = (posting) => {
  if (!posting) return [];

  const rows = [];
  const weeklyPostings = Array.isArray(posting.weeklyPostings) ? posting.weeklyPostings : [];
  const availableSublocations = getAvailableSublocations(posting);

  weeklyPostings.forEach((wp) => {
    const startDate = wp.startDate ?? wp.start;
    const endDate = wp.endDate ?? wp.end;
    const ccCurrentRaw = firstProvided(wp.ccCurrent, wp.creditCardCurrent);
    const ccPostedRaw = firstProvided(wp.ccPosted, wp.creditCardPosted, wp.credit);
    const cashCurrentRaw = firstProvided(wp.cashCurrent);
    const cashPostedRaw = firstProvided(wp.cashPosted, wp.cash);
    const sublocationState = buildSublocationState(
      Array.isArray(wp.sublocations) ? wp.sublocations : [],
      availableSublocations,
    );

    rows.push({
      dateRange: formatDateRange(startDate, endDate),
      postingDate: formatPostingDate(wp.postingDate),
      ...sublocationState,
      postingId: wp.postingId ?? wp.id ?? '—',
      creditCardCurrent: toOptionalNumber(ccCurrentRaw),
      creditCardPosted: toNumber(ccPostedRaw),
      creditVariance: toOptionalNumber(wp.creditVariance),
      cashCurrent: toOptionalNumber(cashCurrentRaw),
      cashPosted: toNumber(cashPostedRaw),
      cashVariance: toOptionalNumber(wp.cashVariance),
      totalCurrent: toOptionalNumber(firstProvided(wp.totalCurrent, wp.total)),
      totalVariance: toOptionalNumber(wp.totalVariance),
    });
  });

  const monthlyStart = posting.startDate;
  const monthlyEnd = posting.endDate;
  const monthlyCreditCardValues = normalizeCreditCardValues(posting);

  rows.push({
    dateRange: formatDateRange(monthlyStart, monthlyEnd),
    ...buildSublocationState(availableSublocations, availableSublocations),
    postingId: posting.postingId ?? posting.id ?? '—',
    creditCardCurrent: toNumber(monthlyCreditCardValues.creditCardCurrent),
    creditCardPosted: toNumber(monthlyCreditCardValues.creditCardPosted),
    creditVariance: toNumber(posting.creditVariance),
    cashCurrent: toNumber(posting.cashCurrent),
    cashPosted: toNumber(posting.cashPosted),
    cashVariance: toNumber(posting.cashVariance),
    totalCurrent: toNumber(posting.totalCurrent),
    totalVariance: toNumber(posting.totalVariance),
    isAggregateRow: true,
  });

  return rows;
};

export const formatFinalizedTimestamp = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format(DATE_TIME_DISPLAY) : '—';
};

/**
 * Enriches assetsVariances with sublocation names from availableSublocations
 */
const enrichAssetsVariancesWithSublocationNames = (assetsVariances = [], availableSublocations = []) => {
  if (!Array.isArray(assetsVariances) || assetsVariances.length === 0) return [];

  const sublocationMap = new Map();
  if (Array.isArray(availableSublocations)) {
    availableSublocations.forEach((sub) => {
      if (sub?.id != null) {
        sublocationMap.set(sub.id, sub.name ?? `Sublocation ${sub.id}`);
      }
    });
  }

  return assetsVariances.map((variance) => ({
    ...variance,
    sublocationName: variance.isBalancingEntry
      ? 'Balancing Entry'
      : sublocationMap.get(variance.sublocationId) || (variance.sublocationId ? `ID: ${variance.sublocationId}` : '—'),
  }));
};

export const buildMonthlyPostingDetailViewModel = (posting, productCategories) => {
  if (!posting) {
    return {
      hasPostingData: false,
      isEmpty: false,
      postingHeader: '—',
      x3ProductName: '—',
      dateRange: '—',
      postingDate: '—',
      finalizedBy: '—',
      finalizedTimestamp: '—',
      tableRows: [],
      assetsVariancesData: [],
    };
  }

  const dataRows = buildTableRowsFromPosting(posting);
  const hasPostingData =
    posting.postingId != null ||
    posting.id != null ||
    posting.startDate != null ||
    posting.endDate != null ||
    posting.x3ProductName != null ||
    posting.x3_productname != null;
  const weeklyPostings = posting?.weeklyPostings ?? [];

  // Enrich assetsVariances with sublocation names
  const assetsVariancesData = enrichAssetsVariancesWithSublocationNames(
    posting.assetsVariances,
    posting.availableSublocations,
  );

  return {
    hasPostingData,
    isEmpty: hasPostingData && Array.isArray(weeklyPostings) && weeklyPostings.length === 0,
    postingHeader: posting.postingId ?? posting.id ?? '—',
    x3ProductName: getMonthlyPostingProductName(posting, productCategories),
    dateRange: `${formatPostingDate(posting.startDate)} - ${formatPostingDate(posting.endDate)}`,
    postingDate: formatPostingDate(posting.postingDate),
    finalizedBy: getMonthlyPostingFinalizedByDisplayName(posting),
    finalizedTimestamp: formatFinalizedTimestamp(posting.updatedAt ?? posting.createdAt),
    tableRows: dataRows,
    assetsVariancesData,
  };
};
